import { SetState } from '@store/store.type'
import { getApiError, getSuccess } from '@store/helper'
import {
  getUser,
  updateUrlAvatarUser,
  UpdateUrlAvatarUserPayloadType,
  updateUser,
  updateUserAddress,
  updateUserSociodemographic,
} from '@api'
import { userApiMapper } from '@mappers'
import { UserType } from '@types'

import { StoreGlobalType } from '../store'

import { UserSliceType } from './user.slice'

/****************************************************************
 * GET USER
 *****************************************************************/

type GetUserActionStatusType = 'getUser/loading' | 'getUser/succeeded' | 'getUser/failed'

export const getUserAction =
  (set: SetState<Partial<UserSliceType & StoreGlobalType>, GetUserActionStatusType>) =>
  async () => {
    try {
      set(
        (state) => {
          state.getUserAction.status = 'LOADING'
          state.error = undefined
          state.success = undefined
        },
        false,
        { type: 'getUser/loading' },
      )

      const userApi = await getUser()
      const user = userApiMapper(userApi)

      set(
        (state) => {
          state.getUserAction.status = 'SUCCEEDED'
          state.user = user
        },
        false,
        { type: 'getUser/succeeded' },
      )
    } catch (e) {
      set(
        (state) => {
          state.getUserAction.status = 'FAILED'
          state.user = undefined
          state.error = getApiError(e)
        },
        false,
        { type: 'getUser/failed' },
      )
    }
  }

/******************************************************************
 * UPDATE USER
 *****************************************************************/

type UpdateUserActionStatusType =
  | 'updateUser/loading'
  | 'updateUser/succeeded'
  | 'updateUser/failed'

export type UpdateUserActionPayloadType = Partial<
  Pick<
    UserType,
    | 'idUser'
    | 'phone'
    | 'lastName'
    | 'firstName'
    | 'nationality'
    | 'birthDate'
    | 'earning'
    | 'study'
    | 'family'
    | 'children'
    | 'job'
    | 'activitySector'
    | 'address'
  >
>

export const updateUserAction =
  (
    set: SetState<
      Partial<UserSliceType & StoreGlobalType>,
      UpdateUserActionStatusType,
      UpdateUserActionPayloadType
    >,
  ) =>
  async (payload: UpdateUserActionPayloadType) => {
    try {
      set(
        (state) => {
          state.updateUserAction.status = 'LOADING'
          state.error = undefined
          state.success = undefined
        },
        false,
        { type: 'updateUser/loading', payload },
      )

      await updateUser({
        idUser: payload?.idUser,
        phone: payload?.phone,
        lastName: payload?.lastName,
        firstName: payload?.firstName,
        birthDate: payload?.birthDate
          ? new Date(payload?.birthDate?.toString()).toISOString()
          : null,
        nationality: payload.nationality,
      })
      await updateUserSociodemographic({
        idUser: payload?.idUser,
        earning: payload?.earning,
        study: payload?.study,
        family: payload?.family,
        children: payload?.children,
        job: payload?.job,
        activitySector: payload?.activitySector,
      })
      const userApi = await updateUserAddress({
        idUser: payload?.idUser,
        address: payload?.address,
      })

      set(
        (state) => {
          state.updateUserAction.status = 'SUCCEEDED'
          state.user = userApiMapper(userApi)
          state.success = getSuccess({
            descriptionT: 'notifications.user.success.description',
          })
        },
        false,
        { type: 'updateUser/succeeded' },
      )
    } catch (e) {
      set(
        (state) => {
          state.updateUserAction.status = 'FAILED'
          state.error = getApiError(e)
        },
        false,
        { type: 'updateUser/failed' },
      )
    }
  }

/******************************************************************
 * UPDATE USER URL AVATAR
 *****************************************************************/

type UpdateUrlAvatarUserActionStatusType =
  | 'updateUrlAvatarUser/loading'
  | 'updateUrlAvatarUser/succeeded'
  | 'updateUrlAvatarUser/failed'

export type UpdateUrlAvatarUserActionPayloadType = UpdateUrlAvatarUserPayloadType

export const updateUrlAvatarUserAction =
  (
    set: SetState<
      Partial<UserSliceType & StoreGlobalType>,
      UpdateUrlAvatarUserActionStatusType,
      UpdateUrlAvatarUserActionPayloadType
    >,
  ) =>
  async (payload: UpdateUrlAvatarUserActionPayloadType) => {
    try {
      set(
        (state) => {
          state.updateUrlAvatarUserAction.status = 'LOADING'
          state.error = undefined
          state.success = undefined
        },
        false,
        { type: 'updateUrlAvatarUser/loading', payload },
      )

      const urlAvatar = await updateUrlAvatarUser(payload)

      set(
        (state) => {
          state.updateUserAction.status = 'SUCCEEDED'
          state.user.urlAvatar = urlAvatar
          state.success = getSuccess({
            descriptionT: 'notifications.user.success.description',
          })
        },
        false,
        { type: 'updateUrlAvatarUser/succeeded' },
      )
    } catch (e) {
      set(
        (state) => {
          state.updateUrlAvatarUserAction.status = 'FAILED'
          state.error = getApiError(e)
        },
        false,
        { type: 'updateUrlAvatarUser/failed' },
      )
    }
  }
